import React, { useState, useEffect } from 'react';
import { array, string, func } from 'prop-types';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Table, TableContainer, styled } from '@nextiva/next-ui-components';
// TODO: fix this in the webpack config
// import UserIcon from '../../icons/user.svg';
import {
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  SvgIcon,
  IconButton,
  StylesProvider,
  makeStyles,
} from '@material-ui/core';
import { pxToRem } from 'utils/pxToRemConverter';
import sassVars from 'styles/application/_base/_variables.scss';

const UserIcon = (props) => {
  return (
    <SvgIcon {...props}>
      <g id="noun_User_398931" transform="translate(-16 -959.407)">
        <path
          id="Path_18271"
          data-name="Path 18271"
          d="M20.739,5a3.974,3.974,0,1,0,3.974,3.974A3.984,3.984,0,0,0,20.739,5Zm0,1.255A2.719,2.719,0,1,1,18.02,8.974,2.71,2.71,0,0,1,20.739,6.255Zm0,7.112a10.605,10.605,0,0,0-5.353,1.327A4.249,4.249,0,0,0,13,18.178V22.27a.628.628,0,0,0,.458.6,27.286,27.286,0,0,0,6.635.954h1.294a27.286,27.286,0,0,0,6.635-.954.628.628,0,0,0,.458-.6V18.178a4.249,4.249,0,0,0-2.386-3.484A10.605,10.605,0,0,0,20.739,13.367Zm0,1.255a9.454,9.454,0,0,1,4.713,1.15,3.037,3.037,0,0,1,1.771,2.405V21.76a26.011,26.011,0,0,1-5.85.811H20.105a26.011,26.011,0,0,1-5.85-.811V18.178a3.037,3.037,0,0,1,1.771-2.405A9.454,9.454,0,0,1,20.739,14.622Z"
          transform="translate(3 954.407)"
          fill="#758ba3"
        />
      </g>
    </SvgIcon>
  );
};

const DragDropIcon = (props) => {
  return (
    <SvgIcon {...props}>
      <g
        id="Group_7480"
        data-name="Group 7480"
        transform="translate(-672 -1366)"
      >
        <line
          id="Line_75"
          data-name="Line 75"
          x2="16"
          transform="translate(672.5 1366.5)"
          fill="none"
          stroke="#758ba3"
          strokeLinecap="round"
          strokeWidth="1"
        />
        <line
          id="Line_76"
          data-name="Line 76"
          x2="16"
          transform="translate(672.5 1372.5)"
          fill="none"
          stroke="#758ba3"
          strokeLinecap="round"
          strokeWidth="1"
        />
        <path
          id="Path_19179"
          data-name="Path 19179"
          d="M0,0H16"
          transform="translate(672.5 1378.5)"
          fill="none"
          stroke="#758ba3"
          strokeLinecap="round"
          strokeWidth="1"
        />
      </g>
    </SvgIcon>
  );
};

const cols = [{ label: 'User' }];

const colsDragDrop = [{ label: 'Order' }, { label: 'User' }];

const Label = styled('div')`
  font-size: ${pxToRem('17px')};
  font-weight: 700;
`;

const StyledTable = styled(Table)`
  min-width: 650;
`;

const useStyles = makeStyles({
  tableRow: {
    display: 'flex',
    height: '76px',
    backgroundColor: 'white',
  },
  tableBody: {
    display: 'block',
    height: 'calc(100vh - 617px)',
    overflow: 'auto',
  },
  tableRowHover: {
    '&:hover': {
      backgroundColor: sassVars.userTableRowHover,
    },
  },
  order: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 0,
    width: '116px',
    color: sassVars.secondaryColor,
    fontFamily: '"lato", sans-serif',
    fontSize: pxToRem('16px'),
    fontWeight: '700',
  },
  user: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 0,
    width: '100%',
    color: sassVars.secondaryColor,
    fontFamily: '"lato", sans-serif',
    fontSize: pxToRem('16px'),
    fontWeight: '700',
  },
  dragDrop: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 0,
    width: '116px',
    fontFamily: '"lato", sans-serif',
  },
  userName: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 0,
    width: '100%',
    fontFamily: '"lato", sans-serif',
  },
  dragDropBtn: {
    marginRight: '24px',
    marginLeft: '4px',
    padding: '0',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  dragDropIcon: {
    width: '17px',
    height: '13px',
  },
  userIcon: {
    marginRight: '12px',
    marginLeft: '8px',
    width: '16px',
    height: '19px',
  },
});

const UserTable = ({
  users = [],
  setUserData = () => {},
  order = true,
  setValue = () => {},
  setSaveCancelOpen = () => {},
}) => {
  const classes = useStyles();

  const [headerData, setHeaderData] = useState(cols);

  useEffect(() => {
    if (order) setHeaderData(colsDragDrop);
    if (!order) setHeaderData(cols);
  }, [users, order]);

  const onDragEnd = (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const userData = [...users];
    const draggable = userData.find((u) => u.userId === draggableId);
    userData.splice(source.index, 1);
    userData.splice(destination.index, 0, draggable);
    setUserData(userData);
    setValue('users', userData);
    setSaveCancelOpen(true);
  };

  return (
    <>
      {users.length > 0 && (
        <TableContainer>
          <Label data-testid="PlatformAdminUI-TeamVoiceSettings-UserTable-UsersHeader">
            Users
          </Label>
          <StyledTable
            aria-label="simple table"
            data-testid="PlatformAdminUI-TeamVoiceSettings-UserTable-Table"
          >
            <StylesProvider injectFirst>
              <TableHead>
                <TableRow className={classes.tableRow}>
                  {headerData.map((col) => {
                    return (
                      <TableCell
                        className={classes[col.label.toLowerCase()]}
                        key={col.label}
                      >
                        {col.label}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <DragDropContext
                onDragEnd={onDragEnd}
                data-testid="PlatformAdminUI-TeamVoiceSettings-UserTable-DragDropContext"
              >
                <Droppable droppableId="user-table-01">
                  {(provided) => (
                    <TableBody
                      innerRef={provided.innerRef}
                      {...provided.droppableProps}
                      className={classes.tableBody}
                    >
                      {users.map((row, i) => (
                        <Draggable
                          key={row.userId}
                          draggableId={row.userId}
                          index={i}
                        >
                          {(draggableProvided) => (
                            <TableRow
                              className={`${classes.tableRow} ${classes.tableRowHover}`}
                              {...draggableProvided.draggableProps}
                              innerRef={draggableProvided.innerRef}
                            >
                              {order && (
                                <TableCell className={classes.dragDrop}>
                                  <IconButton
                                    className={classes.dragDropBtn}
                                    {...draggableProvided.dragHandleProps}
                                    disableRipple
                                  >
                                    <DragDropIcon
                                      className={classes.dragDropIcon}
                                      width="17"
                                      height="13"
                                      viewBox="0 0 17 13"
                                    />
                                  </IconButton>
                                  {i + 1}
                                </TableCell>
                              )}
                              <TableCell
                                className={classes.userName}
                                component="th"
                                scope="row"
                              >
                                <UserIcon
                                  className={classes.userIcon}
                                  width="16"
                                  height="19"
                                  viewBox="0 0 16 19"
                                />
                                {row.fullName}
                              </TableCell>
                            </TableRow>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </TableBody>
                  )}
                </Droppable>
              </DragDropContext>
            </StylesProvider>
          </StyledTable>
        </TableContainer>
      )}
    </>
  );
};

UserTable.propTypes = {
  order: string,
  setSaveCancelOpen: func,
  setUserData: func,
  setValue: func,
  users: array,
};

export default UserTable;
